import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Debounced typeahead select.
 *
 * `fetchOptions(query)` must resolve to an array of
 * `{ id, label, sublabel?, raw? }`. It is called only when the query meets
 * `minChars`, and stale responses are discarded so a slow early request can
 * never overwrite the results of a later keystroke.
 */
function Typeahead({
  id,
  value,
  onSelect,
  fetchOptions,
  placeholder = 'Start typing to search...',
  minChars = 2,
  debounceMs = 300,
  disabled = false,
  emptyMessage = 'No matches found.',
  createOption
}) {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [highlighted, setHighlighted] = useState(-1);

  const containerRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const trimmed = query.trim();

    if (trimmed.length < minChars) {
      setOptions([]);
      setLoading(false);
      setError('');
      return undefined;
    }

    setLoading(true);
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const controller = new AbortController();

    const timer = setTimeout(async () => {
      try {
        const results = await fetchOptions(trimmed, { signal: controller.signal });
        // Ignore responses that arrive after a newer request was issued.
        if (requestIdRef.current !== requestId) return;
        setOptions(Array.isArray(results) ? results : []);
        setError('');
        setHighlighted(-1);
      } catch (err) {
        if (requestIdRef.current !== requestId || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || controller.signal.aborted) return;
        setOptions([]);
        setError(err?.response?.data?.error || err.message || 'Search failed.');
      } finally {
        if (requestIdRef.current === requestId) setLoading(false);
      }
    }, debounceMs);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, minChars, debounceMs, fetchOptions]);

  const choose = useCallback((option) => {
    onSelect(option);
    setQuery('');
    setOptions([]);
    setOpen(false);
    setHighlighted(-1);
  }, [onSelect]);

  const sameEmail = (a, b) => Boolean(a?.raw?.email && b?.raw?.email && a.raw.email.toLowerCase() === b.raw.email.toLowerCase());
  const customOption = !loading && createOption ? createOption(query.trim()) : null;
  const visibleCustomOption = customOption && !options.some(option => option.id === customOption.id || sameEmail(option, customOption)) ? customOption : null;
  const choices = loading ? [] : [...(error ? [] : options), ...(visibleCustomOption ? [visibleCustomOption] : [])];

  const handleKeyDown = (event) => {
    if (!open || choices.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlighted(prev => (prev + 1) % choices.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted(prev => (prev <= 0 ? choices.length - 1 : prev - 1));
    } else if (event.key === 'Enter') {
      if (highlighted >= 0 && highlighted < choices.length) {
        event.preventDefault();
        choose(choices[highlighted]);
      }
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  const showDropdown = open && query.trim().length >= minChars;

  return (
    <div className="typeahead" ref={containerRef}>
      {value && (
        <div className="typeahead-selected">
          <div>
            <strong>{value.label}</strong>
            {value.sublabel && <span className="typeahead-sublabel"> — {value.sublabel}</span>}
          </div>
          <button
            type="button"
            className="typeahead-clear"
            onClick={() => onSelect(null)}
            disabled={disabled}
            aria-label="Clear selection"
          >
            ×
          </button>
        </div>
      )}

      {!value && (
        <>
          <input
            id={id}
            type="text"
            className="typeahead-input"
            value={query}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
          />

          {showDropdown && (
            <div className="typeahead-dropdown">
              {loading && <div className="typeahead-status">Searching…</div>}
              {!loading && error && <div className="typeahead-status typeahead-error">{error}</div>}
              {!loading && !error && options.length === 0 && !visibleCustomOption && (
                <div className="typeahead-status">{emptyMessage}</div>
              )}
              {choices.map((option, index) => (
                <button
                  type="button"
                  key={option.id}
                  className={`typeahead-option${index === highlighted ? ' is-highlighted' : ''}`}
                  onMouseEnter={() => setHighlighted(index)}
                  onClick={() => choose(option)}
                >
                  <span className="typeahead-option-label">{option.label}</span>
                  {option.sublabel && (
                    <span className="typeahead-option-sublabel">{option.sublabel}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {!value && query.trim().length > 0 && query.trim().length < minChars && (
        <div className="typeahead-hint">Type at least {minChars} characters.</div>
      )}
    </div>
  );
}

export default Typeahead;
